import "@mantine/core/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/nprogress/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import {
  ColorSchemeScript,
  Container,
  MantineProvider,
  Paper,
  Stack,
  Title,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications, notifications } from "@mantine/notifications";
import { NavigationProgress, nprogress } from "@mantine/nprogress";
import "./themes/theme.css";
import "mantine-react-table/styles.css";
import * as Sentry from "@sentry/react-router";
import _ from "lodash";
import { useEffect } from "react";
import {
  type MetaFunction,
  data,
  isRouteErrorResponse,
  redirect,
} from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useNavigation,
} from "react-router";
import type { FunctionArgs } from "~/load-context";
import type { Route } from "./+types/root";
import { commitSession, getSession } from "./.server/services/session";
import { theme } from "./themes/theme-with-components";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const env: string | undefined = data?.env?.ENV;
  const titlePrefix = env && env !== "production" ? "🛠️ " : "";
  return [
    { title: `${titlePrefix}RO Admin Panel 🎛️` },
    { name: "description", content: "RO Admin Panel 🎛️" },
  ];
};
export const handle = {
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: "common",
};

export const loader = async (args: FunctionArgs) => {
  const context = args.context;
  const session = await getSession(args);
  const message = session.get("message") || null;
  const error = session.get("error") || null;
  const user = context.USER;
  const env = {
    FLOW_ID_STORY_HELPER_IDEATION:
      context.cloudflare.env.FLOW_ID_STORY_HELPER_IDEATION,
    FLOW_ID_STORY_HELPER_NARRACTIVEEXTRACTOR:
      context.cloudflare.env.FLOW_ID_STORY_HELPER_NARRACTIVEEXTRACTOR,
    FLOW_ID_STORY_CREATE_PDF_MODE:
      context.cloudflare.env.FLOW_ID_STORY_CREATE_PDF_MODE,
    FLOW_ID_STORY_CREATE_BOOKBAND_MODE:
      context.cloudflare.env.FLOW_ID_STORY_CREATE_BOOKBAND_MODE,
    FLOW_ID_STORY_CREATE_NARRATION_CONRTOL_MODE:
      context.cloudflare.env.FLOW_ID_STORY_CREATE_NARRATION_CONRTOL_MODE,
    FLOW_ID_STORY_CREATE_NEWS_MODE:
      context.cloudflare.env.FLOW_ID_STORY_CREATE_NEWS_MODE,
    FLOW_ID_STORY_CREATE_DEFAULT_MODE:
      context.cloudflare.env.FLOW_ID_STORY_CREATE_DEFAULT_MODE,
    ENV: context.cloudflare.env.ENV,
    ASSETS_HOST: context.cloudflare.env.ASSETS_HOST,
    WEB_CLIENT_BASE_URL: context.cloudflare.env.WEB_CLIENT_BASE_URL,
  };

  return data(
    { message, error, user, env },
    {
      headers: {
        "Set-Cookie": await commitSession(context, session),
      },
    }
  );
};

export default function App() {
  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  const { message, error, user } = useLoaderData<any>();
  const { state: navigationState } = useNavigation();

  useEffect(() => {
    if (message) {
      notifications.show({
        title: "Message",
        message: message.message,
        variant: "success",
      });
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      notifications.show({
        title: "Error",
        message: error.message,
        variant: "error",
        color: "red",
      });
    }
  }, [error]);

  useEffect(() => {
    if (navigationState !== "idle") {
      nprogress.start();
    } else {
      nprogress.complete();
    }
  }, [navigationState]);

  return (
    <html lang="en" data-mantine-color-scheme="light">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <Meta />
        <Links />
        <ColorSchemeScript />
        {/* <link rel="manifest" href="manifest.json" /> */}
      </head>
      <body>
        <MantineProvider theme={theme}>
          <NavigationProgress />
          <Notifications position="top-center" />
          <ModalsProvider>
            <Outlet />
          </ModalsProvider>
          <ScrollRestoration />
          <Scripts />
        </MantineProvider>
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;
  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (error && error instanceof Error) {
    // you only want to capture non 404-errors that reach the boundary
    Sentry.captureException(error);
    details = error.message;
    stack = error.stack;
  }
  return (
    <body>
      <MantineProvider defaultColorScheme="light" theme={theme}>
        <Stack p="md">
          <Title order={4}>{message}</Title>
          <code>{details}</code>
          {stack && (
            <pre className="w-full p-4 overflow-x-auto">
              <code>{stack}</code>
            </pre>
          )}
        </Stack>
      </MantineProvider>
    </body>
  );
}
